import { Fragment, useEffect, useState } from 'react';
import api, { fmtRp, errMsg, branchLabel, todayStr } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { BarChart3, Loader2, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

export default function Laporan() {
  const { branches } = useAuth();
  const [tab, setTab] = useState('penjualan');
  const [mode, setMode] = useState('harian');
  const [branch, setBranch] = useState('');
  const [date, setDate] = useState(todayStr());
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [year, setYear] = useState(todayStr().slice(0, 4));
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const computeRange = () => {
    if (mode === 'harian') return { from_date: date, to_date: date, group: 'day' };
    if (mode === 'bulanan') return { from_date: `${month}-01`, to_date: `${month}-31`, group: 'day' };
    if (mode === 'tahunan') return { from_date: `${year}-01-01`, to_date: `${year}-12-31`, group: 'month' };
    return { from_date: from, to_date: to, group: 'day' };
  };

  const load = () => {
    setLoading(true);
    api.get('/reports/sales', { params: { branch, ...computeRange() } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [mode, branch, date, month, year, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const summaryCards = report ? [
    { label: 'Pendapatan', value: fmtRp(report.summary.revenue), cls: 'text-emerald-400' },
    { label: 'Laba Kotor', value: fmtRp(report.summary.profit), cls: 'text-blue-400' },
    { label: 'Item Terjual', value: `${report.summary.items_sold} psg`, cls: 'text-foreground' },
    { label: 'Transaksi', value: report.summary.trx_count, cls: 'text-foreground' },
    { label: 'Total Diskon', value: fmtRp(report.summary.discount), cls: 'text-amber-400' },
    { label: 'Pengeluaran', value: fmtRp(report.summary.expenses), cls: 'text-amber-400' },
    { label: 'Laba Bersih', value: fmtRp(report.summary.net), cls: report.summary.net >= 0 ? 'text-emerald-400' : 'text-brand' },
  ] : [];

  return (
    <div className="space-y-5 animate-fade-up" data-testid="laporan-page">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Laporan</h2>
        <p className="text-sm text-muted-foreground">Penjualan & kas laci — harian, bulanan, tahunan, atau pilih tanggal</p>
      </div>

      <div className="flex gap-2" data-testid="report-tabs">
        {[['penjualan', 'Penjualan'], ['kas', 'Kas Laci']].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`report-tab-${v}`}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${tab === v ? 'bg-brand text-white border-brand shadow-lg shadow-brand/25' : 'bg-surface border-line text-muted-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'penjualan' ? (
        <>
          <div className="lodi-card p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
            <div>
              <span className="lodi-label">Periode</span>
              <div className="flex gap-1.5">
                {[['harian', 'Harian'], ['bulanan', 'Bulanan'], ['tahunan', 'Tahunan'], ['custom', 'Pilih Tanggal']].map(([v, l]) => (
                  <button key={v} onClick={() => setMode(v)} data-testid={`report-mode-${v}`}
                    className={`px-3 py-2 rounded-lg text-xs font-bold border ${mode === v ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {mode === 'harian' && <div><span className="lodi-label">Tanggal</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="lodi-input w-auto" data-testid="report-date-input" /></div>}
            {mode === 'bulanan' && <div><span className="lodi-label">Bulan</span><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="lodi-input w-auto" data-testid="report-month-input" /></div>}
            {mode === 'tahunan' && <div><span className="lodi-label">Tahun</span><input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="lodi-input w-28" data-testid="report-year-input" /></div>}
            {mode === 'custom' && (
              <>
                <div><span className="lodi-label">Dari</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="lodi-input w-auto" data-testid="report-from-input" /></div>
                <div><span className="lodi-label">Sampai</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="lodi-input w-auto" data-testid="report-to-input" /></div>
              </>
            )}
            <div>
              <span className="lodi-label">Cabang</span>
              <select value={branch} onChange={(e) => setBranch(e.target.value)} className="lodi-input w-auto" data-testid="report-branch-select">
                <option value="">Semua Cabang</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {loading || !report ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="report-summary">
                {summaryCards.map((c, i) => (
                  <div key={i} className="lodi-card p-4">
                    <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">{c.label}</p>
                    <p className={`font-display font-extrabold text-sm sm:text-base mt-1 ${c.cls}`}>{c.value}</p>
                  </div>
                ))}
              </div>

              {report.rows.length > 0 && (
                <div className="lodi-card p-5" data-testid="report-chart">
                  <h3 className="font-display font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-brand" /> Grafik Pendapatan</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={report.rows.map((r) => ({ ...r, label: mode === 'tahunan' ? MONTHS[Number(r.period.slice(5)) - 1] : r.period.slice(5) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--linehi)' }} stroke="var(--line)" />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--linehi)' }} stroke="var(--line)" tickFormatter={(v) => `${(v / 1000).toFixed(0)}rb`} />
                      <Tooltip formatter={(v) => fmtRp(v)} contentStyle={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: 'var(--linehi)' }} />
                      <Bar dataKey="revenue" name="Pendapatan" fill="#FF3823" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="profit" name="Laba" fill="#10B981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="lodi-card overflow-hidden" data-testid="report-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line bg-elevated/50">
                        <th className="px-4 py-3">Periode</th>
                        <th className="px-4 py-3 text-center">Trx</th>
                        <th className="px-4 py-3 text-center">Item</th>
                        <th className="px-4 py-3 text-right">Diskon</th>
                        <th className="px-4 py-3 text-right">Pendapatan</th>
                        <th className="px-4 py-3 text-right">Laba</th>
                        <th className="px-4 py-3 text-right">Pengeluaran</th>
                        <th className="px-4 py-3 text-right">Bersih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Tidak ada data pada periode ini</td></tr>}
                      {report.rows.map((r) => (
                        <tr key={r.period} className="border-b border-line/50 last:border-0 hover:bg-elevated/40">
                          <td className="px-4 py-2.5 font-mono font-semibold">{r.period}</td>
                          <td className="px-4 py-2.5 text-center">{r.trx_count}</td>
                          <td className="px-4 py-2.5 text-center">{r.items_sold}</td>
                          <td className="px-4 py-2.5 text-right text-amber-400">{fmtRp(r.discount)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-400">{fmtRp(r.revenue)}</td>
                          <td className="px-4 py-2.5 text-right text-blue-400">{fmtRp(r.profit)}</td>
                          <td className="px-4 py-2.5 text-right text-amber-400">{fmtRp(r.expenses)}</td>
                          <td className={`px-4 py-2.5 text-right font-bold ${r.net >= 0 ? 'text-emerald-400' : 'text-brand'}`}>{fmtRp(r.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <KasLaci />
      )}
    </div>
  );
}

function KasLaci() {
  const { branches } = useAuth();
  const [branch, setBranch] = useState(branches[0]?.id || 'balaraja');
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [closing, setClosing] = useState({ date: todayStr(), opening: '', actual: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    api.get('/reports/cash-drawer', { params: { branch, from_date: from, to_date: to } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [branch, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveClosing = async () => {
    setSaving(true);
    try {
      await api.post('/cash-drawer/closing', {
        branch, date: closing.date,
        opening_cash: Number(closing.opening) || 0,
        actual_cash: closing.actual !== '' ? Number(closing.actual) : null,
        notes: closing.notes,
      });
      toast.success('Kas laci tersimpan');
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="kas-laci-tab">
      <div className="lodi-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <span className="lodi-label">Cabang</span>
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className="lodi-input w-auto" data-testid="kas-branch-select">
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div><span className="lodi-label">Dari</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="lodi-input w-auto" data-testid="kas-from-input" /></div>
        <div><span className="lodi-label">Sampai</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="lodi-input w-auto" data-testid="kas-to-input" /></div>
      </div>

      <div className="lodi-card p-5" data-testid="kas-closing-form">
        <h3 className="font-display font-bold mb-3">Tutup / Audit Kas Laci</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div><span className="lodi-label">Tanggal</span><input type="date" value={closing.date} onChange={(e) => setClosing({ ...closing, date: e.target.value })} className="lodi-input" data-testid="kas-closing-date" /></div>
          <div><span className="lodi-label">Modal Awal (Rp)</span><input type="number" value={closing.opening} onChange={(e) => setClosing({ ...closing, opening: e.target.value })} className="lodi-input" placeholder="0" data-testid="kas-opening-input" /></div>
          <div><span className="lodi-label">Kas Aktual (Rp)</span><input type="number" value={closing.actual} onChange={(e) => setClosing({ ...closing, actual: e.target.value })} className="lodi-input" placeholder="Hitung fisik" data-testid="kas-actual-input" /></div>
          <div><span className="lodi-label">Catatan</span><input value={closing.notes} onChange={(e) => setClosing({ ...closing, notes: e.target.value })} className="lodi-input" data-testid="kas-notes-input" /></div>
          <button onClick={saveClosing} disabled={saving} data-testid="kas-save-button"
            className="py-2.5 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SIMPAN
          </button>
        </div>
      </div>

      <div className="lodi-card overflow-hidden" data-testid="kas-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line bg-elevated/50">
                <th className="px-4 py-3">Tanggal</th>
                <th className="px-4 py-3 text-right">Modal Awal</th>
                <th className="px-4 py-3 text-right">Tunai</th>
                <th className="px-4 py-3 text-right">QRIS</th>
                <th className="px-4 py-3 text-right">Transfer</th>
                <th className="px-4 py-3 text-right">Debit</th>
                <th className="px-4 py-3 text-right">Pengeluaran</th>
                <th className="px-4 py-3 text-right">Seharusnya</th>
                <th className="px-4 py-3 text-right">Aktual</th>
                <th className="px-4 py-3 text-right">Selisih</th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center"><Loader2 className="w-6 h-6 text-brand animate-spin mx-auto" /></td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Tidak ada aktivitas kas pada periode ini</td></tr>
              ) : (
                data.rows.map((r) => (
                  <Fragment key={r.date}>
                    <tr className="border-b border-line/50 hover:bg-elevated/40" data-testid={`kas-row-${r.date}`}>
                      <td className="px-4 py-2.5 font-mono font-semibold">
                        <button onClick={() => setExpanded(expanded === r.date ? null : r.date)} data-testid={`kas-expand-${r.date}`}
                          className="flex items-center gap-1.5 hover:text-brand transition-colors">
                          {expanded === r.date ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          {r.date}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{fmtRp(r.opening)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400">{fmtRp(r.cash_sales)}</td>
                      <td className="px-4 py-2.5 text-right text-blue-400">{fmtRp(r.qris)}</td>
                      <td className="px-4 py-2.5 text-right text-blue-400">{fmtRp(r.transfer)}</td>
                      <td className="px-4 py-2.5 text-right text-violet-400">{fmtRp(r.debit || 0)}</td>
                      <td className="px-4 py-2.5 text-right text-amber-400">{fmtRp(r.expenses)}</td>
                      <td className="px-4 py-2.5 text-right font-bold">{fmtRp(r.expected)}</td>
                      <td className="px-4 py-2.5 text-right">{r.actual != null ? fmtRp(r.actual) : '-'}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${r.difference == null ? 'text-muted-foreground' : r.difference === 0 ? 'text-emerald-400' : 'text-brand'}`}>
                        {r.difference != null ? (r.difference > 0 ? `+${fmtRp(r.difference)}` : fmtRp(r.difference)) : '-'}
                      </td>
                    </tr>
                    {expanded === r.date && (
                      <tr className="border-b border-line/50 bg-elevated/30" data-testid={`kas-expenses-${r.date}`}>
                        <td colSpan={10} className="px-6 py-3">
                          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Pengeluaran hari ini</p>
                          {(r.expense_items || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">Tidak ada pengeluaran</p>
                          ) : (
                            <div className="space-y-1">
                              {r.expense_items.map((e, i) => (
                                <div key={i} className="flex justify-between gap-4 text-xs" data-testid={`kas-expense-item-${r.date}-${i}`}>
                                  <span><span className="capitalize text-muted-foreground">[{e.category}]</span> {e.description || 'Tanpa deskripsi'}</span>
                                  <span className="font-semibold text-amber-400 whitespace-nowrap">-{fmtRp(e.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
